import { useQuery } from '@tanstack/react-query';
import { FileDiff, MessageSquare, Paperclip, Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, type ChangeRequestRow, type Paginated } from '../lib/api';
import { useAuth } from '../lib/auth';
import { statusLabel, useI18n } from '../lib/i18n';
import { formatDate } from '../lib/format';
import { companyName } from '../lib/people';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Pagination,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
} from '../components/ui';

/** The changes the client has asked for, and where each one stands. */
export function ChangeRequestsPage() {
  const { t, locale, localized } = useI18n();
  const { account } = useAuth();
  const company = companyName(account, locale);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['change-requests', page, q],
    queryFn: () =>
      api.get<Paginated<ChangeRequestRow>>('/change-requests', { page, pageSize: 25, q: q.trim() || undefined }),
  });

  return (
    <>
      <PageHeader
        title={t('crs.title')}
        count={data?.total}
        action={
          <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/change-requests/new')}>
            {t('crs.new')}
          </Button>
        }
      />

      <Card padded={false}>
        <div className="p-4">
          <Input
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="max-w-sm"
          />
        </div>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState
            icon={<FileDiff className="h-8 w-8" />}
            title={q ? undefined : t('crs.empty')}
            hint={q ? undefined : t('crs.emptyHint')}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('crs.number')}</Th>
                  <Th>{t('crs.titleField')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th align="end">{t('crs.messages')}</Th>
                  <Th align="end">{t('crs.files')}</Th>
                  <Th align="end">{t('common.created')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((request) => (
                  <Tr key={request.id} onClick={() => navigate(`/change-requests/${request.id}`)}>
                    <Td>
                      <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{request.number}</span>
                    </Td>
                    <Td className="max-w-md">
                      <span className="block truncate">{request.title}</span>
                      <span className="block truncate text-caption text-faint">
                        {request.reviewNote
                          ? `${t('crs.ourNote')}: ${request.reviewNote}`
                          : request.project
                            ? localized(request.project)
                            : t('common.noProject')}
                      </span>
                      {/* Raised by a colleague at the client, or by the company's own sign-in. */}
                      {request.raisedBy && !request.raisedBy.you && (
                        <span className="block truncate text-caption text-faint">
                          {t('crs.raisedByName', { name: request.raisedBy.name ?? company })}
                        </span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={statusTone(request.status)}>{statusLabel(t, 'cr', request.status)}</Badge>
                    </Td>
                    <Td align="end" className="text-muted">
                      <span className="inline-flex items-center gap-1 tabular">
                        <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                        {request.replies}
                      </span>
                    </Td>
                    <Td align="end" className="text-muted">
                      <span className="inline-flex items-center gap-1 tabular">
                        <Paperclip className="h-3.5 w-3.5" aria-hidden />
                        {request.files}
                      </span>
                    </Td>
                    <Td align="end" className="whitespace-nowrap text-caption text-muted">
                      {formatDate(request.createdAt, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>
    </>
  );
}
